import React, { useState, useEffect } from "react"
import { Col, Row, Spinner, Card, CardBody, Table, Badge } from "reactstrap"
import api from "API/Api"
import { Link, useParams } from "react-router-dom"
import QRCode from "react-qr-code"
import SweetAlert from "react-bootstrap-sweetalert"

const CcMobileLogin = () => {
  const { ccId } = useParams()
  const [center, setCenter] = useState(null)
  const [deviceData, setDeviceData] = useState(null)
  const [keys, setKeys] = useState({ reception: null, quality: null, common: null })
  const [genLoading, setGenLoading] = useState({})
  const [revokeConfirm, setRevokeConfirm] = useState(null)
  const [pageLoad, setPageLoad] = useState(true)

  useEffect(() => {
    const load = async () => {
      try {
        const [ccResp, devResp] = await Promise.all([
          api.get("center/" + ccId),
          api.get("device/status/" + ccId + "?center_type=cc")
        ])
        const data = ccResp.data.data?.[0] || ccResp.data.data
        setCenter(data)
        if (devResp.data.success) {
          setDeviceData(devResp.data)
          // Load existing keys for all three login types
          const k = {}
          if (devResp.data.existing_key) k.reception = devResp.data.existing_key
          if (devResp.data.quality_key) k.quality = devResp.data.quality_key
          if (devResp.data.common_key) k.common = devResp.data.common_key
          setKeys(prev => ({ ...prev, ...k }))
        }
      } catch (e) { console.error(e) }
      setPageLoad(false)
    }
    load()
  }, [ccId])

  const generateKey = async (type) => {
    setGenLoading(prev => ({ ...prev, [type]: true }))
    try {
      const resp = await api.post("device/generate-license", {
        cc_id: parseInt(ccId), center_type: 'cc', login_type: type
      })
      if (resp.data.success) {
        setKeys(prev => ({ ...prev, [type]: resp.data.license_key }))
      }
    } catch (e) { console.error(e) }
    setGenLoading(prev => ({ ...prev, [type]: false }))
  }

  const revokeKey = async (type) => {
    setRevokeConfirm(null)
    setGenLoading(prev => ({ ...prev, [type]: true }))
    try {
      const resp = await api.post("device/revoke-license", {
        cc_id: parseInt(ccId), center_type: 'cc', login_type: type
      })
      if (resp.data.success) {
        setKeys(prev => ({ ...prev, [type]: resp.data.license_key }))
      }
    } catch (e) { console.error(e) }
    setGenLoading(prev => ({ ...prev, [type]: false }))
  }

  const ccCode = center ? `${center.plant_id || ""}-CC${center.number || ""}` : ""

  if (pageLoad) return (
    <div className="dashBody"><div className="text-center py-5">
      <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
      <p className="mt-3 text-muted">Loading CC Details...</p>
    </div></div>
  )

  if (!center) return (
    <div className="dashBody"><div className="text-center py-5">
      <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3 d-block"></i>
      <p className="text-muted">Chilling center not found</p>
      <Link to="/chilling-centers" className="btn btn-primary btn-sm">Back to List</Link>
    </div></div>
  )

  // QR Card Component for each login type
  const QRCard = ({ type, label, color, icon }) => {
    const key = keys[type]
    return (
      <div style={{
        background: "#fff", borderRadius: "12px", padding: "20px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)", border: `2px solid ${color}20`,
        textAlign: "center", height: "100%"
      }}>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
          marginBottom: "16px"
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: "8px", background: `${color}15`,
            display: "flex", alignItems: "center", justifyContent: "center"
          }}>
            <i className={`fas ${icon}`} style={{ color, fontSize: "14px" }}></i>
          </div>
          <span style={{ fontWeight: 700, fontSize: "14px", color: "#334155" }}>{label}</span>
        </div>

        {key ? (
          <>
            <div style={{ background: "#fff", borderRadius: "8px", padding: "12px", display: "inline-block", border: "1px solid #e2e8f0" }}>
              <QRCode size={120} value={key} viewBox="0 0 256 256" />
            </div>
            <div style={{
              marginTop: "12px", fontFamily: "monospace", fontSize: "11px", color: color,
              background: `${color}10`, padding: "6px 10px", borderRadius: "6px",
              fontWeight: 600, wordBreak: "break-all", letterSpacing: "0.5px"
            }}>
              {key}
            </div>
          </>
        ) : (
          <div style={{ padding: "30px 10px" }}>
            <i className="fas fa-qrcode fa-3x d-block mb-3" style={{ color: "#e2e8f0" }}></i>
            <p style={{ fontSize: "12px", color: "#94a3b8", marginBottom: 0 }}>No key generated</p>
          </div>
        )}

        <div style={{ marginTop: "12px", display: "flex", gap: "6px", justifyContent: "center" }}>
          {key ? (
            <>
              <button className="btn btn-sm" style={{
                background: `${color}15`, color, border: `1px solid ${color}30`,
                fontSize: "11px", fontWeight: 600, borderRadius: "6px", padding: "4px 12px"
              }}
                onClick={() => { navigator.clipboard.writeText(key) }}
              >
                <i className="fas fa-copy me-1"></i>Copy
              </button>
              <button className="btn btn-sm" style={{
                background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca",
                fontSize: "11px", fontWeight: 600, borderRadius: "6px", padding: "4px 12px"
              }}
                onClick={() => setRevokeConfirm(type)} disabled={genLoading[type]}
              >
                <i className="fas fa-sync-alt me-1"></i>Regenerate
              </button>
            </>
          ) : (
            <button className="btn btn-sm" style={{
              background: color, color: "#fff",
              fontSize: "11px", fontWeight: 600, borderRadius: "6px", padding: "6px 16px"
            }}
              onClick={() => generateKey(type)} disabled={genLoading[type]}
            >
              {genLoading[type] ? <Spinner size="sm" /> : <><i className="fas fa-key me-1"></i>Generate Key</>}
            </button>
          )}
        </div>
      </div>
    )
  }

  // Info item
  const InfoItem = ({ label, value }) => (
    <div style={{ padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
      <div style={{ fontSize: "11px", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</div>
      <div style={{ fontSize: "14px", color: "#1e293b", fontWeight: 600 }}>{value || "—"}</div>
    </div>
  )

  // Rate chart updates
  const rateChartData = [
    { type: "Cow", chart: center.cowRatechart || "N/A", from: "Default", approved: "N/A", time: "" },
    { type: "Buffalo", chart: center.buffaloRatechart || "N/A", from: "Default", approved: "N/A", time: "" },
  ]

  return (
    <div className="dashBody">
      {/* Header */}
      <div className="font-size-18 mb-0 dashHeader">
        <Row>
          <Col md={6}>
            <h4 style={{ margin: 0 }}>
              <i className="fas fa-warehouse" style={{ marginRight: "8px", color: "#0ea5e9" }}></i>
              CC Details — {center.name}
            </h4>
          </Col>
          <Col md={6} style={{ textAlign: "right", display: "flex", justifyContent: "flex-end", gap: "8px", alignItems: "center" }}>
            <Link to={"/cc-mobile-settings/" + ccId}
              style={{ width: "32px", height: "32px", borderRadius: "4px", background: "#6f42c1", color: "#fff",
                display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "14px", textDecoration: "none" }}
              title="Mobile Settings">
              <i className="fas fa-cog"></i>
            </Link>
            <Link to={"/edit-center/" + ccId}
              style={{ width: "32px", height: "32px", borderRadius: "4px", background: "#0d6efd", color: "#fff",
                display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "14px", textDecoration: "none" }}
              title="Edit Center">
              <i className="fas fa-edit"></i>
            </Link>
            <Link to="/chilling-centers" className="dashBtn">
              <i className="fas fa-chevron-left"></i> <b>Back</b>
            </Link>
          </Col>
        </Row>
      </div>

      <div style={{ padding: "16px" }}>
        <Row>
          {/* LEFT COLUMN: CC Info + QR Codes */}
          <Col lg={4}>
            {/* CC Info Card */}
            <Card style={{ border: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", borderRadius: "12px", marginBottom: "16px" }}>
              <CardBody style={{ padding: "20px" }}>
                <h6 style={{ fontWeight: 700, color: "#334155", fontSize: "14px", marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
                  <div style={{ width: 28, height: 28, borderRadius: "6px", background: "#0ea5e915", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <i className="fas fa-warehouse" style={{ color: "#0ea5e9", fontSize: "12px" }}></i>
                  </div>
                  CC Info
                </h6>
                <InfoItem label="CC Number" value={center.number} />
                <InfoItem label="CC Name" value={center.name} />
                <InfoItem label="CC Code" value={ccCode} />
                <InfoItem label="Plant" value={center.plantName ? `${center.plantNumber || ""}-${center.plantName}` : "—"} />
                <InfoItem label="Company" value={center.companyName} />
              </CardBody>
            </Card>

            {/* QR Codes stacked */}
            <div style={{ marginBottom: "16px" }}>
              <QRCard type="reception" label="Reception QR Code" color="#0ea5e9" icon="fa-desktop" />
            </div>
            <div style={{ marginBottom: "16px" }}>
              <QRCard type="quality" label="Quality QR Code" color="#22c55e" icon="fa-flask" />
            </div>
            <div style={{ marginBottom: "16px" }}>
              <QRCard type="common" label="Common QR Code" color="#8b5cf6" icon="fa-qrcode" />
            </div>
          </Col>

          {/* RIGHT COLUMN: Registration Keys + Details */}
          <Col lg={8}>
            {/* Registration Key Details */}
            <Card style={{ border: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", borderRadius: "12px", marginBottom: "16px" }}>
              <CardBody style={{ padding: "20px" }}>
                <h6 style={{ fontWeight: 700, color: "#334155", fontSize: "14px", marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
                  <div style={{ width: 28, height: 28, borderRadius: "6px", background: "#6366f115", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <i className="fas fa-key" style={{ color: "#6366f1", fontSize: "12px" }}></i>
                  </div>
                  Registration Key Details
                </h6>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div style={{ padding: "12px", background: "#f8fafc", borderRadius: "8px" }}>
                    <div style={{ fontSize: "11px", color: "#94a3b8", fontWeight: 600 }}>CC Code</div>
                    <div style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b", fontFamily: "monospace" }}>{ccCode}</div>
                  </div>
                  <div style={{ padding: "12px", background: "#f0f9ff", borderRadius: "8px" }}>
                    <div style={{ fontSize: "11px", color: "#0ea5e9", fontWeight: 600 }}>Reception Key</div>
                    <div style={{ fontSize: "13px", fontWeight: 600, color: "#0c4a6e", fontFamily: "monospace", wordBreak: "break-all" }}>{keys.reception || "Not Generated"}</div>
                  </div>
                  <div style={{ padding: "12px", background: "#f0fdf4", borderRadius: "8px" }}>
                    <div style={{ fontSize: "11px", color: "#22c55e", fontWeight: 600 }}>Quality Key</div>
                    <div style={{ fontSize: "13px", fontWeight: 600, color: "#14532d", fontFamily: "monospace", wordBreak: "break-all" }}>{keys.quality || "Not Generated"}</div>
                  </div>
                  <div style={{ padding: "12px", background: "#f5f3ff", borderRadius: "8px" }}>
                    <div style={{ fontSize: "11px", color: "#8b5cf6", fontWeight: 600 }}>Common Key</div>
                    <div style={{ fontSize: "13px", fontWeight: 600, color: "#3b0764", fontFamily: "monospace", wordBreak: "break-all" }}>{keys.common || "Not Generated"}</div>
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* App Versions */}
            <Card style={{ border: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", borderRadius: "12px", marginBottom: "16px" }}>
              <CardBody style={{ padding: "20px" }}>
                <h6 style={{ fontWeight: 700, color: "#334155", fontSize: "14px", marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
                  <div style={{ width: 28, height: 28, borderRadius: "6px", background: "#f59e0b15", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <i className="fas fa-mobile-alt" style={{ color: "#f59e0b", fontSize: "12px" }}></i>
                  </div>
                  App Versions & Remote Login
                </h6>

                <Row>
                  <Col md={6}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      {[
                        { label: "Reception App Version", value: deviceData?.versions?.reception || "—", color: "#0ea5e9" },
                        { label: "Quality App Version", value: deviceData?.versions?.quality || "—", color: "#22c55e" },
                        { label: "Common App Version", value: deviceData?.versions?.common || "—", color: "#8b5cf6" },
                      ].map((v, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", background: "#f8fafc", borderRadius: "8px" }}>
                          <span style={{ fontSize: "12px", color: "#64748b", fontWeight: 600 }}>{v.label}</span>
                          <Badge style={{ background: v.color, fontSize: "11px", padding: "4px 10px" }}>{v.value}</Badge>
                        </div>
                      ))}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      <div style={{ padding: "10px 12px", background: "#f8fafc", borderRadius: "8px" }}>
                        <div style={{ fontSize: "11px", color: "#94a3b8", fontWeight: 600 }}>Remote Login — Reception</div>
                        <div style={{ fontSize: "13px", color: "#1e293b", fontWeight: 500 }}>{center.remoteAppLogins || "—"}</div>
                      </div>
                      <div style={{ padding: "10px 12px", background: "#f8fafc", borderRadius: "8px" }}>
                        <div style={{ fontSize: "11px", color: "#94a3b8", fontWeight: 600 }}>Remote Login — Quality</div>
                        <div style={{ fontSize: "13px", color: "#1e293b", fontWeight: 500 }}>{center.qualityAppLogins || "—"}</div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* Device Status */}
            {deviceData?.devices?.filter(d => d.is_active)?.length > 0 && (
              <Card style={{ border: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", borderRadius: "12px", marginBottom: "16px" }}>
                <CardBody style={{ padding: "20px" }}>
                  <h6 style={{ fontWeight: 700, color: "#334155", fontSize: "14px", marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
                    <div style={{ width: 28, height: 28, borderRadius: "6px", background: "#10b98115", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <i className="fas fa-tablet-alt" style={{ color: "#10b981", fontSize: "12px" }}></i>
                    </div>
                    Registered Devices
                  </h6>
                  <div className="table-responsive">
                    <Table className="table-hover mb-0" style={{ fontSize: "13px" }}>
                      <thead style={{ background: "#f8fafc" }}>
                        <tr>
                          {["Device", "Type", "Status", "Last Sync", "App Ver"].map(h => (
                            <th key={h} style={{ fontWeight: 600, color: "#64748b", fontSize: "11px", textTransform: "uppercase" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {deviceData.devices.filter(d => d.is_active).map(dev => (
                          <tr key={dev.id}>
                            <td>
                              <div style={{ fontWeight: 600, color: "#1e293b" }}>{dev.device_name || "Unknown"}</div>
                              <div style={{ fontSize: "10px", color: "#94a3b8", fontFamily: "monospace" }}>{dev.device_uuid ? dev.device_uuid.substring(0, 16) + "..." : "—"}</div>
                            </td>
                            <td><Badge color={dev.login_type === "reception" ? "info" : dev.login_type === "quality" ? "success" : "purple"} style={{ fontSize: "10px" }}>{dev.login_type || "—"}</Badge></td>
                            <td>
                              <Badge color={dev.is_online ? "success" : "secondary"} style={{ fontSize: "11px", padding: "4px 8px" }}>
                                <i className="fas fa-circle me-1" style={{ fontSize: "6px", verticalAlign: "middle" }}></i>
                                {dev.is_online ? "Online" : "Offline"}
                              </Badge>
                            </td>
                            <td style={{ fontSize: "12px", color: "#64748b" }}>{dev.last_sync_at ? new Date(dev.last_sync_at).toLocaleDateString() : "Never"}</td>
                            <td><span style={{ fontSize: "11px", background: "#f0f9ff", color: "#0369a1", padding: "2px 6px", borderRadius: "4px" }}>{dev.app_version || "—"}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            )}

            {/* Rate Chart Updates */}
            <Card style={{ border: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", borderRadius: "12px", marginBottom: "16px" }}>
              <CardBody style={{ padding: "20px" }}>
                <h6 style={{ fontWeight: 700, color: "#334155", fontSize: "14px", marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
                  <div style={{ width: 28, height: 28, borderRadius: "6px", background: "#e11d4815", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <i className="fas fa-chart-bar" style={{ color: "#e11d48", fontSize: "12px" }}></i>
                  </div>
                  Rate Chart Updates
                </h6>
                <div className="table-responsive">
                  <Table className="table mb-0" style={{ fontSize: "13px" }}>
                    <thead style={{ background: "#f8fafc" }}>
                      <tr>
                        {["Milk Type", "Rate Chart", "Effective From", "Approved", "Approved Time"].map(h => (
                          <th key={h} style={{ fontWeight: 600, color: "#64748b", fontSize: "11px", textTransform: "uppercase", padding: "10px 12px" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rateChartData.map((r, i) => (
                        <tr key={i}>
                          <td style={{ padding: "10px 12px", fontWeight: 600 }}>
                            <span style={{
                              background: r.type === "Cow" ? "#f0f9ff" : "#fdf4ff",
                              color: r.type === "Cow" ? "#0369a1" : "#86198f",
                              padding: "3px 10px", borderRadius: "4px", fontSize: "12px"
                            }}>{r.type}</span>
                          </td>
                          <td style={{ padding: "10px 12px", color: "#64748b" }}>{r.chart}</td>
                          <td style={{ padding: "10px 12px", color: "#64748b" }}>{r.from}</td>
                          <td style={{ padding: "10px 12px", color: "#64748b" }}>{r.approved}</td>
                          <td style={{ padding: "10px 12px", color: "#64748b" }}>{r.time || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Revoke Confirm */}
      {revokeConfirm && (
        <SweetAlert title="Regenerate Key?" warning showCancel
          confirmBtnText="Regenerate" confirmBtnBsStyle="danger" cancelBtnBsStyle="secondary"
          onConfirm={() => revokeKey(revokeConfirm)} onCancel={() => setRevokeConfirm(null)}>
          <p>This will invalidate the current <b>{revokeConfirm}</b> key and generate a new one.</p>
          <p style={{ color: "#dc2626", fontWeight: 600 }}>The old device will NOT be able to sync until re-activated.</p>
        </SweetAlert>
      )}
    </div>
  )
}

export default CcMobileLogin
